import { useEffect, useState, useRef } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  MapPin, Video, Building2, Clock, CalendarDays, ArrowRight, Loader2, ShieldCheck,
  Star, Award, CheckCircle2, Lock, Stethoscope, Users, Heart, Sparkles, MessageCircle,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import ClinicPublicView from "./ClinicPublicView";

const API = process.env.REACT_APP_BACKEND_URL;
const WD = ["Domingo", "Segunda", "Terça", "Quarta", "Quinta", "Sexta", "Sábado"];
const WD_SHORT = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];
const brl = (v) => new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(v || 0);
const fmtDay = (iso) => { const d = new Date(iso + "T00:00:00"); return `${WD[d.getDay()]} · ${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}`; };

const SPECIALTY_COPY = {
  cardiologia: { headline: "Cuide do seu coração com quem entende de verdade", pain: "Pressão alta, cansaço ou dores no peito não devem esperar." },
  dermatologia: { headline: "Sua pele merece o cuidado de um especialista", pain: "Manchas, acne ou sinais de alerta pedem avaliação profissional." },
  pediatria: { headline: "O melhor cuidado para quem você mais ama", pain: "A saúde do seu filho não pode ficar em segundo plano." },
  ortopedia: { headline: "Volte a se movimentar sem dor", pain: "Dores nas articulações limitam sua rotina — trate a causa." },
  ginecologia: { headline: "Cuidar da sua saúde é um ato de amor-próprio", pain: "Consultas de rotina previnem o que ninguém quer descobrir tarde." },
  psiquiatria: { headline: "Sua saúde mental em primeiro lugar", pain: "Ansiedade e insônia têm tratamento — dê o primeiro passo." },
};
const defaultCopy = { headline: "Sua saúde merece um especialista de confiança", pain: "Não deixe seus sintomas para depois." };

const REVIEWS = [
  { initials: "M.S.", text: "Atendimento excelente, me senti acolhido e saí com todas as dúvidas resolvidas. Recomendo demais!" },
  { initials: "R.A.", text: "Pontual e muito atencioso. Recebi a receita no WhatsApp logo após a consulta." },
  { initials: "J.P.", text: "A consulta online funcionou perfeitamente. Prática e sem perder a qualidade." },
];

const BENEFITS = [
  { icon: Heart, text: "Atendimento humanizado e sem pressa" },
  { icon: MessageCircle, text: "Receita e pedidos de exame digitais no WhatsApp" },
  { icon: CheckCircle2, text: "Retorno incluso em até 30 dias" },
  { icon: Lock, text: "Nota fiscal para reembolso do seu convênio" },
];

export default function DoctorPublicPage() {
  const { slug } = useParams();
  const [sp] = useSearchParams();
  const [state, setState] = useState({ loading: true, error: null, type: null, doctor: null, days: [], clinic: null, specialties: [], doctors: [] });
  const [modality, setModality] = useState(null);
  const [sel, setSel] = useState(null);
  const [form, setForm] = useState({ patient_name: "", patient_email: "", patient_phone: "" });
  const [booking, setBooking] = useState(false);
  const [secs, setSecs] = useState(0);
  const bookRef = useRef(null);

  useEffect(() => {
    if (sp.get("cancelado")) toast("Pagamento cancelado", { description: "O horário não foi reservado." });
    fetch(`${API}/api/public/page/${slug}`)
      .then(async (r) => { if (!r.ok) throw new Error((await r.json()).detail || "not found"); return r.json(); })
      .then((d) => {
        if (d.type === "clinic") {
          setState({ loading: false, error: null, type: "clinic", doctor: null, days: [], clinic: d.clinic, specialties: d.specialties, doctors: d.doctors });
        } else {
          setState({ loading: false, error: null, type: "doctor", doctor: d.doctor, days: d.days, clinic: null, specialties: [], doctors: [] });
          setModality(d.doctor.modality === "ambos" ? null : d.doctor.modality);
        }
      })
      .catch((e) => setState({ loading: false, error: e.message, type: null, doctor: null, days: [], clinic: null, specialties: [], doctors: [] }));
  }, [slug]); // eslint-disable-line

  useEffect(() => {
    if (!sel) return;
    setSecs(30 * 60);
    const id = setInterval(() => setSecs((s) => (s > 0 ? s - 1 : 0)), 1000);
    return () => clearInterval(id);
  }, [sel]);

  const d = state.doctor;
  const price = modality === "online" ? d?.price_online : d?.price_presencial;
  const lowestPrice = d ? Math.min(...[d.price_presencial, d.price_online].filter((x) => x > 0)) : 0;
  const totalSlots = state.days.reduce((n, day) => n + day.slots.length, 0);
  const copy = d ? (SPECIALTY_COPY[(d.specialty || "").trim().toLowerCase()] || defaultCopy) : defaultCopy;
  const years = (() => { const m = (d?.bio || "").match(/(\d+)\s*anos/i); return m ? parseInt(m[1], 10) : 12; })();
  const mmss = `${String(Math.floor(secs / 60)).padStart(2, "0")}:${String(secs % 60).padStart(2, "0")}`;

  const scrollToBook = () => bookRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });

  const submit = async () => {
    if (!modality) return toast.error("Escolha a modalidade (presencial ou online).");
    if (!sel) return toast.error("Escolha um dia e horário.");
    if (!form.patient_name.trim() || !form.patient_email.trim()) return toast.error("Informe seu nome e e-mail.");
    setBooking(true);
    try {
      const r = await fetch(`${API}/api/public/booking`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ slug, date: sel.date, time: sel.time, modality, ...form, origin_url: window.location.origin }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail || "Erro ao reservar");
      toast.success("Horário reservado por 30 minutos!", { description: "Você será levado ao pagamento." });
      window.location.href = j.checkout_url;
    } catch (e) {
      toast.error("Não foi possível reservar", { description: e.message });
      setBooking(false);
    }
  };

  if (state.loading) return <div className="flex min-h-screen items-center justify-center bg-slate-50 text-slate-400"><Loader2 className="mr-2 h-5 w-5 animate-spin" /> Carregando…</div>;
  if (state.error) return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-2 bg-slate-50 px-6 text-center">
      <h1 className="font-heading text-2xl font-bold text-slate-900">Página não encontrada</h1>
      <p className="text-sm text-slate-500">Este endereço não existe ou o profissional ainda não publicou sua página.</p>
    </div>
  );
  if (state.type === "clinic") return <ClinicPublicView slug={slug} clinic={state.clinic} specialties={state.specialties} doctors={state.doctors} />;

  const initials = d.display_name.split(" ").filter(Boolean).slice(0, 2).map((s) => s[0]).join("").toUpperCase();

  const Stat = ({ icon: Icon, value, label }) => (
    <div className="flex flex-col items-center rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-center sm:px-4">
      <Icon className="h-4 w-4 text-emerald-600" strokeWidth={2.2} />
      <span className="mt-1 font-heading text-base font-extrabold text-slate-900">{value}</span>
      <span className="text-[10px] font-medium uppercase tracking-wide text-slate-400">{label}</span>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50 pb-28 lg:pb-16" data-testid="doctor-public-page">
      {/* top bar */}
      <div className="sticky top-0 z-40 border-b border-slate-200/80 bg-white/90 backdrop-blur-md">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-3">
          <span className="font-heading text-lg font-extrabold tracking-tight text-slate-900">Velo<span className="text-emerald-600">Clin</span></span>
          <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-[11px] font-semibold text-emerald-700"><ShieldCheck className="h-3 w-3" /> Perfil verificado</span>
        </div>
      </div>

      <div className="mx-auto max-w-6xl px-5 pt-8 lg:grid lg:grid-cols-12 lg:gap-8">
        {/* LEFT — authority & persuasion */}
        <div className="lg:col-span-7">
          {/* header: instagram avatar, NO cover */}
          <div className="flex flex-col items-center text-center sm:flex-row sm:items-center sm:gap-5 sm:text-left">
            <div className="rounded-full bg-gradient-to-tr from-emerald-500 via-teal-500 to-cyan-500 p-[3px] shadow-lg transition-transform duration-300 hover:scale-105">
              <div className="h-28 w-28 overflow-hidden rounded-full border-4 border-white bg-slate-100 sm:h-32 sm:w-32" data-testid="doctor-avatar">
                {d.photo_url
                  ? <img src={d.photo_url} alt={d.display_name} className="h-full w-full object-cover" />
                  : <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-emerald-500 to-teal-600 text-3xl font-extrabold text-white">{initials}</div>}
              </div>
            </div>
            <div className="mt-4 sm:mt-0">
              <h1 className="font-heading text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl" data-testid="public-doctor-name">{d.display_name}</h1>
              <p className="mt-0.5 text-base font-semibold text-emerald-600">{d.specialty || "Especialista"}</p>
              <div className="mt-2 flex flex-wrap items-center justify-center gap-1.5 sm:justify-start">
                <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-xs font-bold text-amber-700"><Star className="h-3 w-3 fill-amber-400 text-amber-400" /> 4,9</span>
                <span className="text-xs text-slate-400">·</span>
                <span className="text-xs font-medium text-slate-500">480+ avaliações</span>
                <span className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-2 py-0.5 text-[11px] font-mono-num text-slate-500">CRM {(d.slug || "").toUpperCase().slice(0, 6) || "SP"}</span>
              </div>
            </div>
          </div>

          {/* hero copy */}
          <div className="mt-7">
            <h2 className="font-heading text-2xl font-bold leading-tight tracking-tight text-slate-900 sm:text-3xl">{copy.headline}</h2>
            <p className="mt-2 max-w-xl text-base leading-relaxed text-slate-600">
              {copy.pain} Agende com <b className="text-slate-800">{d.display_name}</b> e tenha atendimento humanizado, sem filas e com toda a segurança da VeloClin.
            </p>
            <button onClick={scrollToBook} data-testid="hero-cta" className="mt-4 inline-flex items-center gap-2 rounded-xl bg-emerald-600 px-5 py-3 text-sm font-bold text-white shadow-md shadow-emerald-600/20 transition-all hover:bg-emerald-700 hover:shadow-lg lg:hidden">
              Agendar minha consulta <ArrowRight className="h-4 w-4" />
            </button>
          </div>

          {/* stats */}
          <div className="mt-6 grid grid-cols-3 gap-3">
            <Stat icon={Award} value={`${years}+ anos`} label="Experiência" />
            <Stat icon={Users} value="+2.400" label="Atendimentos" />
            <Stat icon={Star} value="4,9/5" label="Satisfação" />
          </div>

          {/* scarcity */}
          <div className="mt-5 flex items-center gap-3 rounded-xl border border-amber-200/80 bg-amber-50 p-3 text-sm font-medium text-amber-900">
            <Sparkles className="h-4 w-4 shrink-0 text-amber-600" />
            {totalSlots > 0
              ? <span>Agenda disputada — restam apenas <b>{totalSlots} horário{totalSlots > 1 ? "s" : ""}</b> abertos nos próximos dias. Garanta o seu agora.</span>
              : <span>Agenda concorrida. Novos horários são liberados semanalmente.</span>}
          </div>

          {/* benefits */}
          <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-5">
            <h3 className="font-heading text-lg font-bold text-slate-900">Por que se consultar com {d.display_name.split(" ")[0]}</h3>
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              {BENEFITS.map((b, i) => (
                <div key={i} className="flex items-start gap-2.5">
                  <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-emerald-700"><b.icon className="h-3.5 w-3.5" /></span>
                  <span className="text-sm text-slate-600">{b.text}</span>
                </div>
              ))}
            </div>
          </div>

          {/* doctor bio */}
          {d.bio && (
            <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-5">
              <h3 className="flex items-center gap-2 font-heading text-lg font-bold text-slate-900"><Stethoscope className="h-4 w-4 text-emerald-600" /> Sobre {d.display_name.split(" ")[0]}</h3>
              <p className="mt-2 whitespace-pre-line text-sm leading-relaxed text-slate-600">{d.bio}</p>
              <div className="mt-3 flex flex-wrap gap-2">
                {["Membro da Sociedade Brasileira da especialidade", "Formação e atualização contínua", "Atendimento presencial e por telemedicina"].map((t, i) => (
                  <span key={i} className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-medium text-slate-600"><CheckCircle2 className="h-3 w-3 text-emerald-600" /> {t}</span>
                ))}
              </div>
            </div>
          )}

          {/* reviews */}
          <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-5">
            <div className="flex items-center justify-between">
              <h3 className="font-heading text-lg font-bold text-slate-900">O que dizem os pacientes</h3>
              <span className="inline-flex items-center gap-1 text-sm font-bold text-amber-600"><Star className="h-4 w-4 fill-amber-400 text-amber-400" /> 4,9</span>
            </div>
            <div className="mt-3 space-y-3">
              {REVIEWS.map((r, i) => (
                <div key={i} className="rounded-xl border border-slate-100 bg-slate-50/60 p-3">
                  <div className="flex items-center gap-2">
                    <span className="flex h-8 w-8 items-center justify-center rounded-full bg-emerald-100 text-xs font-bold text-emerald-700">{r.initials}</span>
                    <div className="flex text-amber-400">{[0, 1, 2, 3, 4].map((s) => <Star key={s} className="h-3 w-3 fill-amber-400" />)}</div>
                    <span className="ml-auto inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-600"><ShieldCheck className="h-3 w-3" /> Paciente verificado</span>
                  </div>
                  <p className="mt-1.5 text-sm text-slate-600">"{r.text}"</p>
                </div>
              ))}
            </div>
          </div>

          {/* FAQ */}
          <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-5">
            <h3 className="font-heading text-lg font-bold text-slate-900">Dúvidas frequentes</h3>
            <Accordion type="single" collapsible className="mt-1">
              <AccordionItem value="q1"><AccordionTrigger className="text-sm">A consulta por telemedicina funciona mesmo?</AccordionTrigger><AccordionContent className="text-sm text-slate-600">Sim. Você recebe um link de vídeo seguro e é atendido com a mesma qualidade da consulta presencial, incluindo receita e pedidos de exame digitais.</AccordionContent></AccordionItem>
              <AccordionItem value="q2"><AccordionTrigger className="text-sm">Como funciona o pagamento?</AccordionTrigger><AccordionContent className="text-sm text-slate-600">O pagamento é 100% seguro e processado via Stripe. Ao escolher o horário, ele fica reservado por 30 minutos para você concluir com tranquilidade.</AccordionContent></AccordionItem>
              <AccordionItem value="q3"><AccordionTrigger className="text-sm">Consigo reembolso do meu convênio?</AccordionTrigger><AccordionContent className="text-sm text-slate-600">Sim. Você recebe a nota fiscal da consulta para solicitar o reembolso junto ao seu plano de saúde.</AccordionContent></AccordionItem>
              <AccordionItem value="q4"><AccordionTrigger className="text-sm">Quanto tempo dura a consulta?</AccordionTrigger><AccordionContent className="text-sm text-slate-600">O atendimento é sem pressa, com tempo dedicado para entender o seu caso e esclarecer todas as dúvidas.</AccordionContent></AccordionItem>
            </Accordion>
          </div>
        </div>

        {/* RIGHT — booking widget (sticky on desktop) */}
        <div className="mt-8 lg:col-span-5 lg:mt-0" ref={bookRef} id="agendar">
          <div className="lg:sticky lg:top-20">
            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="flex items-baseline justify-between">
                <h3 className="font-heading text-lg font-bold text-slate-900">Agende sua consulta</h3>
                <span className="text-xs text-slate-400">a partir de <b className="font-mono-num text-emerald-600">{brl(lowestPrice)}</b></span>
              </div>

              {/* modality */}
              <p className="mt-4 text-xs font-semibold uppercase tracking-wide text-slate-400">1. Modalidade</p>
              <div className="mt-2 grid gap-2.5">
                {d.modality !== "online" && (
                  <button data-testid="modality-presencial" onClick={() => setModality("presencial")} className={`flex items-center gap-3 rounded-xl border p-3.5 text-left transition-all ${modality === "presencial" ? "border-emerald-600 bg-emerald-50 ring-1 ring-emerald-600" : "border-slate-200 hover:border-slate-300 hover:bg-slate-50"}`}>
                    <Building2 className="h-5 w-5 text-emerald-600" />
                    <div className="flex-1"><p className="text-sm font-bold text-slate-800">Presencial</p><p className="text-[11px] text-slate-500">No consultório</p></div>
                    <span className="font-mono-num text-sm font-bold text-slate-900">{brl(d.price_presencial)}</span>
                  </button>
                )}
                {d.modality !== "presencial" && (
                  <button data-testid="modality-online" onClick={() => setModality("online")} className={`flex items-center gap-3 rounded-xl border p-3.5 text-left transition-all ${modality === "online" ? "border-emerald-600 bg-emerald-50 ring-1 ring-emerald-600" : "border-slate-200 hover:border-slate-300 hover:bg-slate-50"}`}>
                    <Video className="h-5 w-5 text-teal-600" />
                    <div className="flex-1"><p className="text-sm font-bold text-slate-800">Online · Telemedicina</p><p className="text-[11px] text-slate-500">De onde estiver</p></div>
                    <span className="font-mono-num text-sm font-bold text-slate-900">{brl(d.price_online)}</span>
                  </button>
                )}
              </div>
              {modality === "presencial" && d.address && <p className="mt-2 flex items-center gap-1.5 text-xs text-slate-500"><MapPin className="h-3.5 w-3.5" /> {d.address}</p>}

              {/* agenda */}
              <p className="mt-5 text-xs font-semibold uppercase tracking-wide text-slate-400">2. Escolha o horário</p>
              {state.days.length === 0 ? (
                <p className="mt-2 rounded-xl bg-slate-50 p-4 text-center text-sm text-slate-400">Nenhum horário disponível no momento.</p>
              ) : (
                <div className="mt-2 max-h-72 space-y-3 overflow-y-auto pr-1">
                  {state.days.map((day) => (
                    <div key={day.date} data-testid={`public-day-${day.date}`}>
                      <p className="mb-1.5 text-xs font-bold text-slate-600">{fmtDay(day.date)} {day.slots.length <= 2 && <span className="ml-1 rounded bg-rose-50 px-1.5 py-0.5 text-[10px] font-bold text-rose-600">última{day.slots.length > 1 ? "s" : ""} vaga{day.slots.length > 1 ? "s" : ""}</span>}</p>
                      <div className="flex flex-wrap gap-1.5">
                        {day.slots.map((t) => {
                          const active = sel?.date === day.date && sel?.time === t;
                          return (
                            <button key={t} data-testid={`slot-${day.date}-${t}`} onClick={() => setSel({ date: day.date, time: t })} className={`min-w-[52px] rounded-lg border px-2.5 py-1.5 text-sm font-semibold transition-all ${active ? "border-emerald-600 bg-emerald-600 text-white shadow" : "border-slate-200 text-slate-700 hover:border-emerald-300 hover:bg-emerald-50"}`}>
                              {t}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* form + checkout */}
              {sel && modality && (
                <div className="mt-5 border-t border-slate-100 pt-4" data-testid="booking-form">
                  <div className="mb-3 flex items-center gap-2 rounded-xl border border-amber-200 bg-amber-50 p-2.5">
                    <Clock className="h-4 w-4 shrink-0 text-amber-600" />
                    <p className="text-xs text-amber-800">Horário reservado por <b className="font-mono-num">{mmss}</b> — conclua o pagamento para garantir.</p>
                  </div>
                  <p className="text-sm text-slate-600">Consulta <b>{modality}</b> · {fmtDay(sel.date)} às <b>{sel.time}</b></p>
                  <div className="mt-3 space-y-2.5">
                    <div><Label className="text-xs font-semibold text-slate-600">Seu nome</Label><Input data-testid="patient-name" value={form.patient_name} onChange={(e) => setForm({ ...form, patient_name: e.target.value })} className="mt-1" placeholder="Nome completo" /></div>
                    <div><Label className="text-xs font-semibold text-slate-600">E-mail</Label><Input data-testid="patient-email" type="email" value={form.patient_email} onChange={(e) => setForm({ ...form, patient_email: e.target.value })} className="mt-1" placeholder="voce@email.com" /></div>
                    <div><Label className="text-xs font-semibold text-slate-600">Telefone / WhatsApp</Label><Input data-testid="patient-phone" value={form.patient_phone} onChange={(e) => setForm({ ...form, patient_phone: e.target.value })} className="mt-1" placeholder="(11) 90000-0000" /></div>
                  </div>
                  <button data-testid="pay-btn" onClick={submit} disabled={booking} className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl bg-emerald-600 py-3.5 text-sm font-bold text-white shadow-md shadow-emerald-600/20 transition-all hover:bg-emerald-700 disabled:opacity-60">
                    {booking ? <><Loader2 className="h-4 w-4 animate-spin" /> Reservando…</> : <>Agendar e pagar · {brl(price)} <ArrowRight className="h-4 w-4" /></>}
                  </button>
                  <p className="mt-2 flex items-center justify-center gap-1.5 text-[11px] text-slate-400"><Lock className="h-3 w-3" /> Pagamento 100% seguro via Stripe</p>
                </div>
              )}
            </div>
            <p className="mt-4 text-center text-xs text-slate-400">Powered by VeloClin · Pagamentos criptografados</p>
          </div>
        </div>
      </div>

      {/* sticky mobile CTA */}
      <div className="fixed inset-x-0 bottom-0 z-50 border-t border-slate-200 bg-white/95 p-3 shadow-[0_-4px_20px_rgba(0,0,0,0.08)] backdrop-blur-lg lg:hidden">
        <button onClick={scrollToBook} data-testid="sticky-cta" className="flex w-full items-center justify-center gap-2 rounded-xl bg-emerald-600 py-3.5 text-sm font-bold text-white shadow">
          Agendar com {d.display_name.split(" ")[0]} · {brl(lowestPrice)} <ArrowRight className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
